import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  MenuItem,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { API_ENDPOINTS, EXPENSE_CLAIMS_ENABLED } from '../constants/api';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AppLayout from '../components/AppLayout';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import type { User } from '../types';
import { formatDate, utcMs } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotUpdate, couldNotDelete, couldNotCreate, permissionCopy, genericCopy } from '../utils/userFacingCopy';
import { SortableTableHeader } from '../utils/sortableTableHeader';

interface Company {
  id: string;
  name: string;
  countryCode: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

interface SupportedCountryOption {
  code: string;
  name: string;
}

interface BusinessUnit {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

interface Department {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

interface ExpenseCategory {
  id: string;
  glCode: string;
  name: string;
  createdAt: string;
  updatedAt?: string;
  /** Number of expense claim line items using this category. Explains why delete is disabled. */
  lineItemCount?: number;
}

type TabValue = 'companies' | 'business-units' | 'departments' | 'expense-categories';
type SortField = 'name' | 'description' | 'createdAt' | 'assignedUsers' | 'glCode';

export default function ReferenceDataPage() {
  const navigate = useNavigate();
  const { displayTimeZoneId } = useAuth();
  const [activeTab, setActiveTab] = useState<TabValue>('companies');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Company | BusinessUnit | Department | ExpenseCategory | null>(null);
  const [formData, setFormData] = useState({ name: '', description: '', glCode: '', countryCode: 'ZA' });
  const [supportedCountries, setSupportedCountries] = useState<SupportedCountryOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [userCounts, setUserCounts] = useState<{
    companies: Record<string, number>;
    businessUnits: Record<string, number>;
    departments: Record<string, number>;
  }>({
    companies: {},
    businessUnits: {},
    departments: {},
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Check if user is admin
      const userResponse = await apiService.get<{ roles: string[] }>(API_ENDPOINTS.AUTH.ME);
      const currentUser = userResponse.data;
      if (!currentUser.roles.includes('Administrator')) {
        setError(permissionCopy.administrator);
        return;
      }

      const [companiesRes, businessUnitsRes, departmentsRes, usersRes] = await Promise.all([
        apiService.get<Company[]>(API_ENDPOINTS.REFERENCE_DATA.COMPANIES.BASE),
        apiService.get<BusinessUnit[]>(API_ENDPOINTS.REFERENCE_DATA.BUSINESS_UNITS.BASE),
        apiService.get<Department[]>(API_ENDPOINTS.REFERENCE_DATA.DEPARTMENTS.BASE),
        apiService.get<User[]>(API_ENDPOINTS.USER_MANAGEMENT.BASE).catch(() => ({ data: [] as User[] })), // Fallback to empty array if fails
      ]);

      setCompanies(companiesRes.data);
      setBusinessUnits(businessUnitsRes.data);
      setDepartments(departmentsRes.data);

      if (EXPENSE_CLAIMS_ENABLED) {
        const expenseCategoriesRes = await apiService.get<ExpenseCategory[]>(
          API_ENDPOINTS.REFERENCE_DATA.EXPENSE_CATEGORIES.BASE
        );
        setExpenseCategories(expenseCategoriesRes.data);
      } else {
        setExpenseCategories([]);
      }

      // Calculate user counts for each reference data item
      const activeUsers = usersRes.data.filter((u) => u.isActive);
      
      const companyCounts: Record<string, number> = {};
      companiesRes.data.forEach((company) => {
        companyCounts[company.id] = activeUsers.filter((u) => u.companyId === company.id).length;
      });

      const businessUnitCounts: Record<string, number> = {};
      businessUnitsRes.data.forEach((businessUnit) => {
        businessUnitCounts[businessUnit.id] = activeUsers.filter((u) => u.businessUnitId === businessUnit.id).length;
      });

      const departmentCounts: Record<string, number> = {};
      departmentsRes.data.forEach((department) => {
        departmentCounts[department.id] = activeUsers.filter((u) => {
          // Check both old singular departmentId (for backward compatibility) and new plural departmentIds
          return u.departmentId === department.id || 
                 u.departmentIds?.includes(department.id);
        }).length;
      });

      setUserCounts({
        companies: companyCounts,
        businessUnits: businessUnitCounts,
        departments: departmentCounts,
      });
    } catch (err: unknown) {
      console.error('Failed to load reference data:', err);
      setError(getApiErrorMessage(err, genericCopy.referenceData));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    apiService
      .get<{ countries: SupportedCountryOption[] }>('/System/supported-countries')
      .then((res) => setSupportedCountries(Array.isArray(res.data?.countries) ? res.data.countries : []))
      .catch(() => setSupportedCountries([]));
  }, []);

  const countryOptions =
    supportedCountries.length > 0
      ? supportedCountries
      : [
          { code: 'ZA', name: 'South Africa' },
          { code: 'GB', name: 'United Kingdom' },
          { code: 'IN', name: 'India' },
        ];

  useEffect(() => {
    if (!EXPENSE_CLAIMS_ENABLED && activeTab === 'expense-categories') {
      setActiveTab('companies');
    }
  }, [activeTab]);

  const getCurrentData = useCallback(() => {
    switch (activeTab) {
      case 'companies':
        return companies;
      case 'business-units':
        return businessUnits;
      case 'departments':
        return departments;
      case 'expense-categories':
        return expenseCategories;
    }
  }, [activeTab, companies, businessUnits, departments, expenseCategories]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedData = useCallback(() => {
    const data = getCurrentData();
    if (!data || data.length === 0) return data;

    const getItemUserCount = (item: Company | BusinessUnit | Department | ExpenseCategory): number => {
      switch (activeTab) {
        case 'companies':
          return userCounts.companies[item.id] ?? 0;
        case 'business-units':
          return userCounts.businessUnits[item.id] ?? 0;
        case 'departments':
          return userCounts.departments[item.id] ?? 0;
        case 'expense-categories':
          return (item as ExpenseCategory).lineItemCount ?? 0;
      }
    };

    const sorted = [...data].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'description':
          aValue = ('description' in a && a.description) ? a.description.toLowerCase() : '';
          bValue = ('description' in b && b.description) ? b.description.toLowerCase() : '';
          break;
        case 'createdAt':
          aValue = utcMs(a.createdAt);
          bValue = utcMs(b.createdAt);
          break;
        case 'assignedUsers':
          aValue = getItemUserCount(a);
          bValue = getItemUserCount(b);
          break;
        case 'glCode':
          aValue = ('glCode' in a ? a.glCode : '').toLowerCase();
          bValue = ('glCode' in b ? b.glCode : '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [activeTab, sortField, sortDirection, userCounts, getCurrentData]);

  const getEndpoint = () => {
    switch (activeTab) {
      case 'companies':
        return API_ENDPOINTS.REFERENCE_DATA.COMPANIES;
      case 'business-units':
        return API_ENDPOINTS.REFERENCE_DATA.BUSINESS_UNITS;
      case 'departments':
        return API_ENDPOINTS.REFERENCE_DATA.DEPARTMENTS;
      case 'expense-categories':
        return API_ENDPOINTS.REFERENCE_DATA.EXPENSE_CATEGORIES;
    }
  };

  const getTitle = () => {
    switch (activeTab) {
      case 'companies':
        return 'Companies';
      case 'business-units':
        return 'Business Units';
      case 'departments':
        return 'Departments';
      case 'expense-categories':
        return 'Expense Categories';
    }
  };

  const getSingularTitle = () => {
    switch (activeTab) {
      case 'companies':
        return 'Company';
      case 'business-units':
        return 'Business Unit';
      case 'departments':
        return 'Department';
      case 'expense-categories':
        return 'Expense Category';
    }
  };

  const isExpenseCategoriesTab = activeTab === 'expense-categories';

  const handleCreateClick = () => {
    setSelectedItem(null);
    setFormData({ name: '', description: '', glCode: '', countryCode: 'ZA' });
    setCreateDialogOpen(true);
  };

  const handleEditClick = (item: Company | BusinessUnit | Department | ExpenseCategory) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      description: 'description' in item ? (item.description || '') : '',
      glCode: 'glCode' in item ? item.glCode : '',
      countryCode: 'countryCode' in item ? item.countryCode : 'ZA',
    });
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (item: Company | BusinessUnit | Department | ExpenseCategory) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleCreate = async () => {
    if (isExpenseCategoriesTab) {
      if (!formData.glCode.trim() || !formData.name.trim()) {
        setError('GL Code and Name are required');
        return;
      }
    } else if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const endpoint = getEndpoint();
      const payload = isExpenseCategoriesTab
        ? { glCode: formData.glCode.trim(), name: formData.name.trim() }
        : {
          name: formData.name.trim(),
          description: formData.description || undefined,
          ...(activeTab === 'companies' ? { countryCode: formData.countryCode } : {}),
        };
      await apiService.post(endpoint.BASE, payload);
      setCreateDialogOpen(false);
      setFormData({ name: '', description: '', glCode: '', countryCode: 'ZA' });
      loadData();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotCreate('the item')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedItem) return;
    if (isExpenseCategoriesTab) {
      if (!formData.glCode.trim() || !formData.name.trim()) {
        setError('GL Code and Name are required');
        return;
      }
    } else if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const endpoint = getEndpoint();
      const payload = isExpenseCategoriesTab
        ? { glCode: formData.glCode.trim(), name: formData.name.trim() }
        : {
          name: formData.name.trim(),
          description: formData.description || undefined,
          ...(activeTab === 'companies' ? { countryCode: formData.countryCode } : {}),
        };
      await apiService.put(endpoint.BY_ID(selectedItem.id), payload);
      setEditDialogOpen(false);
      setSelectedItem(null);
      setFormData({ name: '', description: '', glCode: '', countryCode: 'ZA' });
      loadData();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotUpdate('the item')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;

    // Get user count for the selected item
    const userCount = (() => {
      switch (activeTab) {
        case 'companies':
          return userCounts.companies[selectedItem.id] ?? 0;
        case 'business-units':
          return userCounts.businessUnits[selectedItem.id] ?? 0;
        case 'departments':
          return userCounts.departments[selectedItem.id] ?? 0;
        case 'expense-categories':
          return (selectedItem as ExpenseCategory).lineItemCount ?? 0;
      }
    })();

    // Should not happen if UI is correct, but double-check
    if (userCount > 0) {
      setError(isExpenseCategoriesTab
        ? `Cannot delete expense category "${selectedItem.name}" because it is used on ${userCount} line item${userCount !== 1 ? 's' : ''}. Remove or change the category on those line items before deleting.`
        : `Cannot delete ${getSingularTitle().toLowerCase()} "${selectedItem.name}" because it is assigned to ${userCount} user${userCount !== 1 ? 's' : ''}. Please unassign all users before deleting.`);
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      return;
    }

    try {
      setDeleting(true);
      setError('');
      const endpoint = getEndpoint();
      await apiService.delete(endpoint.BY_ID(selectedItem.id));
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      loadData();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, couldNotDelete('the item')));
    } finally {
      setDeleting(false);
    }
  };

  const getSelectedItemUserCount = (): number => {
    if (!selectedItem) return 0;
    switch (activeTab) {
      case 'companies':
        return userCounts.companies[selectedItem.id] ?? 0;
      case 'business-units':
        return userCounts.businessUnits[selectedItem.id] ?? 0;
      case 'departments':
        return userCounts.departments[selectedItem.id] ?? 0;
      case 'expense-categories':
        return (selectedItem as ExpenseCategory).lineItemCount ?? 0;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !companies.length && !businessUnits.length && !departments.length && !expenseCategories.length) {
    return (
      <Box sx={{ p: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mb: 2, ml: -1 }} title="Back">
          <ArrowBackIcon />
        </IconButton>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const sortedData = getSortedData();

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <BusinessIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Reference Data Management
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Manage organisational reference data including companies, business units, and departments. These are used to organise users and generate reports.
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="Companies" value="companies" />
              <Tab label="Business Units" value="business-units" />
              <Tab label="Departments" value="departments" />
              {EXPENSE_CLAIMS_ENABLED && <Tab label="Expense Categories" value="expense-categories" />}
            </Tabs>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              sx={{ ml: 2, textTransform: 'none' }}
            >
              Add {getSingularTitle()}
            </Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table key={displayTimeZoneId}>
              <TableHead>
                <TableRow>
                  {isExpenseCategoriesTab && (
                    <TableCell>
                      <SortableTableHeader
                        label="GL Code"
                        active={sortField === 'glCode'}
                        direction={sortField === 'glCode' ? sortDirection : 'asc'}
                        onClick={() => handleSort('glCode')}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <SortableTableHeader
                      label="Name"
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDirection : 'asc'}
                      onClick={() => handleSort('name')}
                    />
                  </TableCell>
                  {!isExpenseCategoriesTab && (
                    <TableCell>
                      <SortableTableHeader
                        label={activeTab === 'companies' ? 'Country' : 'Description'}
                        active={sortField === 'description'}
                        direction={sortField === 'description' ? sortDirection : 'asc'}
                        onClick={() => handleSort('description')}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <SortableTableHeader
                      label="Created"
                      active={sortField === 'createdAt'}
                      direction={sortField === 'createdAt' ? sortDirection : 'asc'}
                      onClick={() => handleSort('createdAt')}
                    />
                  </TableCell>
                  <TableCell>
                    <SortableTableHeader
                      label={isExpenseCategoriesTab ? 'Line Items' : 'Assigned Users'}
                      active={sortField === 'assignedUsers'}
                      direction={sortField === 'assignedUsers' ? sortDirection : 'asc'}
                      onClick={() => handleSort('assignedUsers')}
                    />
                  </TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No {getTitle().toLowerCase()} found
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedData.map((item) => {
                    const userCount = (() => {
                      switch (activeTab) {
                        case 'companies':
                          return userCounts.companies[item.id] ?? 0;
                        case 'business-units':
                          return userCounts.businessUnits[item.id] ?? 0;
                        case 'departments':
                          return userCounts.departments[item.id] ?? 0;
                        case 'expense-categories':
                          return (item as ExpenseCategory).lineItemCount ?? 0;
                      }
                    })();
                    
                    return (
                      <TableRow key={item.id}>
                        {isExpenseCategoriesTab && (
                          <TableCell>{'glCode' in item ? item.glCode : '-'}</TableCell>
                        )}
                        <TableCell>{item.name}</TableCell>
                        {!isExpenseCategoriesTab && (
                          <TableCell>{activeTab === 'companies' && 'countryCode' in item ? item.countryCode : ('description' in item ? (item.description || '-') : '-')}</TableCell>
                        )}
                        <TableCell>{formatDate(item.createdAt, { includeTime: true })}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontWeight={userCount > 0 ? 'medium' : 'normal'} color={userCount > 0 ? 'error.main' : 'text.secondary'}>
                              {userCount}
                            </Typography>
                          </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => handleEditClick(item)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(item)} 
                            color="error"
                            disabled={userCount > 0}
                            title={userCount > 0 ? (isExpenseCategoriesTab ? `Cannot delete: used on ${userCount} line item${userCount !== 1 ? 's' : ''}` : `Cannot delete: assigned to ${userCount} user${userCount !== 1 ? 's' : ''}`) : 'Delete'}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create {getSingularTitle()}</DialogTitle>
        <DialogContent>
          {isExpenseCategoriesTab && (
            <TextField
              fullWidth
              label="GL Code"
              value={formData.glCode}
              onChange={(e) => setFormData({ ...formData, glCode: e.target.value })}
              margin="normal"
              required
            />
          )}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          {!isExpenseCategoriesTab && activeTab === 'companies' && (
            <TextField
              select
              fullWidth
              label="Country Code"
              value={formData.countryCode}
              onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
              margin="normal"
              required
            >
              {countryOptions.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </MenuItem>
              ))}
            </TextField>
          )}
          {!isExpenseCategoriesTab && (
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={submitting} sx={{ textTransform: 'none' }}>
            {submitting ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit {getSingularTitle()}</DialogTitle>
        <DialogContent>
          {isExpenseCategoriesTab && (
            <TextField
              fullWidth
              label="GL Code"
              value={formData.glCode}
              onChange={(e) => setFormData({ ...formData, glCode: e.target.value })}
              margin="normal"
              required
            />
          )}
          <TextField
            fullWidth
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
            required
          />
          {!isExpenseCategoriesTab && activeTab === 'companies' && (
            <TextField
              select
              fullWidth
              label="Country Code"
              value={formData.countryCode}
              onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
              margin="normal"
              required
            >
              {countryOptions.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </MenuItem>
              ))}
            </TextField>
          )}
          {!isExpenseCategoriesTab && (
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleEdit} variant="contained" disabled={submitting} sx={{ textTransform: 'none' }}>
            {submitting ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => { setDeleteDialogOpen(false); setSelectedItem(null); }}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle>
            {selectedItem && getSelectedItemUserCount() > 0
              ? `Cannot Delete ${getSingularTitle()}`
              : `Delete ${getSingularTitle()}`}
          </DialogTitle>
          <DialogContent>
            {selectedItem && (() => {
              const usageCount = getSelectedItemUserCount();
              if (usageCount > 0) {
                return (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body1" fontWeight="600" gutterBottom>
                      {isExpenseCategoriesTab
                        ? <>This expense category cannot be deleted because it is used on <strong>{usageCount}</strong> line item{usageCount !== 1 ? 's' : ''} in expense claims.</>
                        : <>This {getSingularTitle().toLowerCase()} cannot be deleted because it is currently assigned to <strong>{usageCount}</strong> user{usageCount !== 1 ? 's' : ''}.</>
                      }
                    </Typography>
                    <Typography variant="body2">
                      {isExpenseCategoriesTab
                        ? <>Remove or change the category on those line items (in the relevant expense claims) before deleting the category &quot;{selectedItem.name}&quot;.</>
                        : <>Please unassign all users from the {getSingularTitle().toLowerCase()} &quot;{selectedItem.name}&quot; before attempting to delete it. You can do this by changing the {getSingularTitle().toLowerCase()} for each assigned user on the User Management page.</>
                      }
                    </Typography>
                  </Alert>
                );
              }
              return (
                <>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Are you sure you want to delete the {getSingularTitle().toLowerCase()} <strong>"{selectedItem.name}"</strong>?
                  </Typography>
                  <Alert severity="warning">
                    This action cannot be undone.
                  </Alert>
                </>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => { setDeleteDialogOpen(false); setSelectedItem(null); }} 
              sx={{ textTransform: 'none' }}
            >
              {selectedItem && getSelectedItemUserCount() > 0 ? 'Close' : 'Cancel'}
            </Button>
            {selectedItem && getSelectedItemUserCount() === 0 && (
              <Button 
                onClick={handleDelete} 
                variant="contained" 
                color="error" 
                disabled={deleting} 
                sx={{ textTransform: 'none' }}
              >
                {deleting ? <CircularProgress size={20} /> : 'Delete'}
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </AppLayout>
  );
}
